import React, { useEffect, useRef, useState } from 'react';
import { Loader2, RotateCcw, Sparkles, X } from 'lucide-react';
import {
  CROP_ASPECT_OPTIONS,
  DEFAULT_POLISH,
  buildPreviewSource,
  computeAutoWhiteBalance,
  exportPolishedBlob,
  loadImage,
  polishIsIdentity,
  renderPolishToCanvas,
  type CropAspect,
  type PolishState,
} from '../services/imagePolish';

interface PolishNightModalProps {
  imageUrl: string;
  onClose: () => void;
  onSave: (file: File) => Promise<void>;
}

function SliderRow({
  label,
  value,
  min = -100,
  max = 100,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-300">{label}</span>
        <span className="text-[11px] text-slate-500 tabular-nums">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-indigo-500"
      />
    </label>
  );
}

export default function PolishNightModal({ imageUrl, onClose, onSave }: PolishNightModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewSourceRef = useRef<HTMLCanvasElement | null>(null);
  const [polish, setPolish] = useState<PolishState>(DEFAULT_POLISH);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const img = await loadImage(imageUrl);
        if (cancelled) return;
        previewSourceRef.current = buildPreviewSource(img);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load image');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const source = previewSourceRef.current;
    const canvas = canvasRef.current;
    if (!source || !canvas || loading) return;
    renderPolishToCanvas(source, polish, canvas);
  }, [polish, loading]);

  const updateTone = (key: keyof PolishState['tone'], value: number) => {
    setPolish((prev) => ({ ...prev, tone: { ...prev.tone, [key]: value } }));
  };

  const updateColour = (key: keyof PolishState['colour'], value: number) => {
    setPolish((prev) => ({ ...prev, colour: { ...prev.colour, [key]: value } }));
  };

  const handleAutoWb = () => {
    const source = previewSourceRef.current;
    if (!source) return;
    const { temperature, tint } = computeAutoWhiteBalance(source);
    setPolish((prev) => ({
      ...prev,
      colour: { ...prev.colour, temperature, tint },
    }));
  };

  const handleSave = async () => {
    if (polishIsIdentity(polish)) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const blob = await exportPolishedBlob(imageUrl, polish);
      const file = new File([blob], `hyperdusk-polished-${Date.now()}.jpg`, { type: 'image/jpeg' });
      await onSave(file);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save polished image');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div>
            <p className="text-sm font-semibold text-white">Polish night version</p>
            <p className="text-xs text-slate-500">Tone, white balance, and crop before animation</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr,280px] gap-0 overflow-hidden">
          <div className="bg-slate-900/60 flex items-center justify-center p-4 overflow-auto min-h-[280px]">
            {loading ? (
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            ) : (
              <canvas ref={canvasRef} className="max-w-full max-h-[60vh] rounded shadow-lg" />
            )}
          </div>

          <div className="border-t lg:border-t-0 lg:border-l border-slate-800 p-4 space-y-5 overflow-y-auto">
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wide text-slate-400">Tone</p>
              <SliderRow label="Exposure" value={polish.tone.exposure} onChange={(v) => updateTone('exposure', v)} />
              <SliderRow label="Brightness" value={polish.tone.brightness} onChange={(v) => updateTone('brightness', v)} />
              <SliderRow label="Contrast" value={polish.tone.contrast} onChange={(v) => updateTone('contrast', v)} />
              <SliderRow label="Highlights" value={polish.tone.highlights} onChange={(v) => updateTone('highlights', v)} />
              <SliderRow label="Shadows" value={polish.tone.shadows} onChange={(v) => updateTone('shadows', v)} />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-slate-400">Colour</p>
                <button
                  type="button"
                  onClick={handleAutoWb}
                  className="flex items-center gap-1 text-[11px] text-indigo-300 hover:text-indigo-200"
                >
                  <Sparkles className="w-3 h-3" />
                  Auto WB
                </button>
              </div>
              <SliderRow label="Temperature" value={polish.colour.temperature} onChange={(v) => updateColour('temperature', v)} />
              <SliderRow label="Tint" value={polish.colour.tint} onChange={(v) => updateColour('tint', v)} />
              <SliderRow label="Saturation" value={polish.colour.saturation} onChange={(v) => updateColour('saturation', v)} />
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-slate-400">Crop</p>
              <div className="flex flex-wrap gap-1.5">
                {CROP_ASPECT_OPTIONS.map((opt) => {
                  const selected = polish.cropAspect === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setPolish((prev) => ({ ...prev, cropAspect: opt.id as CropAspect }))}
                      className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                        selected
                          ? 'border-indigo-500 bg-indigo-900/40 text-white'
                          : 'border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {error && <p className="text-xs text-red-400">{error}</p>}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-800">
          <button
            type="button"
            onClick={() => setPolish(DEFAULT_POLISH)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-300 bg-slate-800 rounded-lg hover:bg-slate-700"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-800 rounded-lg hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {saving ? 'Saving…' : 'Save as variant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
